// Olympe
import { CloudObject } from 'olympe';
import { useProperty } from '@olympeio/core';

// Rxjs
import { combineLatest, map, distinctUntilChanged } from 'rxjs';

// Internal
import { defaultWrap } from './wrappers';
import { reorderObject, warn } from './helpers';

/**
 * Init the columns
 * @param {!BrickContext} $ 
 * @param {!Observable} _wrappers 
 * @return {!Observable}
 */
export function initFilteredColumns($, _wrappers) {
    // Internal states
    $.set('_rgColumnWidth', {}); // Column width overrides
    $.set('_rgColumnOrder', {}); // Column order (rank) overrides

    // Listen to the config and the _wrappers first object properties
    return combineLatest([
        $.observe('Config', false),
        _wrappers
            .pipe(map(wrappers => (wrappers[0] ?? defaultWrap()).__rgProperties()))
            .pipe(distinctUntilChanged()),
        $.observe('Enable Column Reordering', false),
        $.observe('_rgColumnWidth'),
        $.observe('_rgColumnOrder'),
        $.observe('Default Width')
    ])

        // Build the columns
        .pipe(map(([config, properties, ...others]) => {
            // Config is set by the user
            if (config) {
                return [config, ...others];
            }

            // Config is auto-generated from the data
            if (properties.length > 0) {
                return [
                    properties.map((path, index) => ({ 'Property Path': path, Type: 'AUTO', Rank: index })),
                    ...others
                ];
            }

            // Empty config
            return [[], ...others];
        }))

        // Transform to an array of objects
        .pipe(map(([config, reorderableColumns, columnWidth, columnOrder, defaultWidth]) => {
            return config
                .map(column => column instanceof CloudObject ? column.toObject(true, true) : column)
                .sort((a, b) => getColumnRank(a, columnOrder) - getColumnRank(b, columnOrder))
                .map(column => generateColumn(column, reorderableColumns, columnWidth, defaultWidth));
        }))

        // Cache the filtered columns (per level) for quick access
        .pipe(map(columns => {
            const filteredColumns = filterLevelledColumns(columns);
            $.set('_rgFilteredColumns', filteredColumns);
            return filteredColumns;
        }));
}

/**
 * @param {!BrickContext} $
 * @return {!*}
 */
export function useColumns($) {
    const filteredColumns = useProperty($, '_rgFilteredColumns');
    return filteredColumns?.get(0) ?? [];
}

/**
 * @param {!BrickContext} $
 * @param {number} level 
 * @return {!Array}
 */
export function getColumns($, level) {
    const filteredColumns = $.get('_rgFilteredColumns');
    return filteredColumns?.get(level ?? 0) ?? [];
}

/**
 * Handle the resizing of columns
 * @param {!BrickContext} $ 
 * @param {string} columnId 
 * @param {number} width 
 */
export function handleOnColumnResized($, columnId, width) {
    const currentCols = { ...$.get('_rgColumnWidth') };
    currentCols[columnId] = width;
    $.set('_rgColumnWidth', currentCols);
}

/**
 * @param {!Array} columns
 * @return {!Map<number, !Array>}
 */
function filterLevelledColumns(columns) {
    // Group the columns by rank and save all levels
    const levels = new Set();
    const perRank = columns.reduce((acc, cur) => {
        const rank = cur.Rank ?? 0;
        if(!acc.has(rank)) {
            acc.set(rank, []);
        }
        acc.get(rank).push(cur);
        levels.add(cur.Level ?? 0);
        return acc;
    }, new Map());
    const allRanks = Array.from(perRank.keys());

    // Get the columns for each level
    const filterLevel = level => {
        return allRanks.map(rank => {
            const rankColumns = perRank.get(rank);
            const atLevel = rankColumns.find(column => (column.Level ?? 0) === level);
            const generic = rankColumns.find(column => (column.Level ?? 0) === -1);
            return atLevel ?? generic ?? rankColumns[0];
        });
    };

    // Return all levels
    return Array.from(levels.values()).reduce((acc, cur) => acc.set(cur, filterLevel(cur)), new Map());
}

/**
 * @param {!Object} column
 * @param {boolean} reorderableColumns
 * @param {!Object} columnWidth
 * @param {number} defaultWidth
 * @return {!Object}
 */
function generateColumn(column, reorderableColumns, columnWidth, defaultWidth) {
    if(typeof column.Rank !== 'number') {
        warn(`column rank is missing, using 0 as fallback (column: ${column.Header ?? column['Property Path']})`);
    }
    const rank = column.Rank ?? 0;
    return {
        columnId: rank,
        reorderable: reorderableColumns,
        width: columnWidth?.[rank] ?? column.Width ?? defaultWidth ?? 150,
        resizable: column.Resizable,
        Level: column.Level ?? 0,
        ...column
    };
}

/**
 * @param {!BrickContext} $
 * @param {number} targetColumnId
 * @param {!Array} columnIds
 */
export function handleOnColumnsReordered($, targetColumnId, columnIds) {
    const columns = getColumns($, 0);
    const ranks = columns.map(column => column.Rank);
    const targetIndex = ranks.indexOf(targetColumnId);
    const movingIndexes = columnIds.map(id => ranks.indexOf(id));
    const newRanks = reorderObject(ranks, movingIndexes, targetIndex);
    const newOrder = newRanks.reduce((acc, cur, index) => ({ [cur]: index, ...acc }), {});
    $.set('_rgColumnOrder', newOrder);
}

/**
 * @param {!Object} column
 * @param {!Object} overrides
 * @return {number}
 */
function getColumnRank(column, overrides) {
    return overrides[column.Rank] ?? column.Rank;
}
